/**
 * Client auth — talks to the worker's stateless `/api/auth/*` endpoints.
 * Replaces Better Auth's React client.
 *
 * The session is a signed cookie carried automatically by the browser
 * (`credentials: "include"`); `useSession` just reads `/api/auth/session`.
 * `name` / `image` ride along in the cookie when the identity provider
 * supplied them (Google does; the dev sign-in does not).
 */
import { useQuery } from "@tanstack/react-query";

import { clearPersistedQueryCache } from "./query-client";

export interface SessionUser {
  id: string;
  email: string;
  name?: string;
  image?: string;
  /** How the user signed in (`"google"` / `"dev"`); shown on `/me`. */
  provider?: "google" | "dev";
}

export interface Session {
  user: SessionUser;
}

export const SESSION_QUERY_KEY = ["auth", "session"] as const;

/** Signed out is `200 {user: null}`, so a non-ok status is an error, not a
 *  verdict: throw, and let React Query retry rather than report a sign-out. */
export async function fetchSession(): Promise<Session | null> {
  const res = await fetch("/api/auth/session", { credentials: "include" });
  if (!res.ok) throw new Error(`session lookup failed (${res.status})`);
  const body = (await res.json()) as { user: SessionUser | null };
  return body.user ? { user: body.user } : null;
}

export function useSession(): { data: Session | null; isPending: boolean } {
  const q = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    staleTime: 30_000,
    // Kept in memory (and persisted) for a week so a cold launch rehydrates the
    // session and renders the shell immediately; the 30s staleTime still drives
    // a background revalidation on mount that catches an expired cookie.
    gcTime: 1000 * 60 * 60 * 24 * 7,
  });
  return { data: q.data ?? null, isPending: q.isPending };
}

/**
 * Dev-only: mint a local session. Throws on failure.
 *
 * Pass a `username` to impersonate a distinct user (handy for testing
 * multi-user features like presence); a bare username becomes
 * `<username>@hearth.local`. Omit it to sign in as the default dev user.
 */
export async function devSignIn(username?: string): Promise<void> {
  const trimmed = username?.trim();
  const res = await fetch("/api/auth/dev-sign-in", {
    method: "POST",
    credentials: "include",
    ...(trimmed
      ? {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username: trimmed }),
        }
      : {}),
  });
  if (!res.ok) throw new Error(`Dev sign-in failed (${res.status})`);
}

export async function signOut(): Promise<void> {
  await fetch("/api/auth/sign-out", {
    method: "POST",
    credentials: "include",
  });
  // Drop the persisted boot cache before reloading so the next user on this
  // device never rehydrates the previous user's identity/onboarding state.
  clearPersistedQueryCache();
  // `/sign-in` is a page load with no session, which is what makes local-store
  // collect every member's chats. Nothing to clean up here.
  window.location.assign("/sign-in");
}
